import MaterialTable from '@material-table/core'
import ClearSharpIcon from '@mui/icons-material/ClearSharp'
import DvrSharpIcon from '@mui/icons-material/DvrSharp'
import { Box, Button, Paper } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { confirmAlert } from 'react-confirm-alert'
import 'react-confirm-alert/src/react-confirm-alert.css'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { SupplierRegionType } from '../../../api/models/Common/JyrneyEnums'
import { Supplier } from '../../../api/supplier/suppliers.types'
import { suppliersAPI } from '../../../api/supplier/suppliersAPI'
import { BaseLayout } from '../../../layouts/Base/BaseLayout'
import styles from './SupplierSearchPageStyle'

const mapSupplierToArray = (supplier: Supplier) => {
  let regionName = ''
  if (supplier.regionName) {
    regionName = (SupplierRegionType as any)[supplier.regionName]
  }
  const data = {
    supplierId: supplier.id,
    accountNumber: supplier.accountNumber ? supplier.accountNumber : '',
    companyName: supplier.companyName ? supplier.companyName : '',
    alias: supplier.alias ? supplier.alias : '',
    registrationNumber: supplier.registrationNumber
      ? supplier.registrationNumber
      : '',
    regionName: regionName,
    accountManager: supplier.accountManager ? supplier.accountManager : '',
    status: supplier.status ? supplier.status : '',
  }
  return data
}
const getTableData = (suppliers: Array<Supplier> | undefined) => {
  const data: Array<object | JSX.Element> = []
  if (suppliers) {
    suppliers.forEach((supplier) => {
      const mapped = mapSupplierToArray(supplier)
      data.push(mapped)
    })
  }
  return data
}

const SupplierSearchPage = () => {
  const history = useHistory()
  const currentUser = useSelector((state: any) => state.users.user)
  const theme = useTheme() as any
  const headerBackgroundColor =
    theme.components?.journeyStatus?.headerColour ?? theme.palette.grey[300]
  const headerTextColor =
    theme.components?.journeyStatus?.headerTextColour ??
    theme.palette.getContrastText(headerBackgroundColor)

  const { isLoading, data } = useQuery({
    queryKey: ['fetchSuppliers'],
    queryFn: async () => {
      if (currentUser.roles.includes('Jyrney_Admin')) {
        return await suppliersAPI.getSuppliers()
      } else {
        return await suppliersAPI.searchSuppliers()
      }
    },
    refetchOnWindowFocus: false,
  })
  const tableData = getTableData(data?.data.value)

  const handleEditClick = (id: number) => {
    if (id) {
      history.push(`/admin/supplier/${id}`)
    }
  }

  const { mutateAsync: mutate } = useMutation({
    mutationFn: (id: number) => {
      return suppliersAPI.deleteSupplier(id)
    },
  })

  const handleDeleteClick = (event: any, supplierId: number) => {
    if (supplierId) {
      event.preventDefault()
      mutate(supplierId).then(() => {
        history.push(`/admin/supplier/search`)
      })
    }
  }

  const handleClickOpenCancel = async (event: any, supplier: any) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <Paper elevation={3} sx={styles.customCancelBookingContainer}>
            <Box component={'h1'} sx={styles.customCancelBookingHeader}>
              Delete Confirmation
            </Box>

            <Box component={'p'} sx={styles.customCancelBookingMessage}>
              Are you sure you want to delete supplier:{' '}
              {supplier.registrationNumber} - {supplier.companyName}?
            </Box>
            <div style={{ textAlign: 'right' }}>
              <Button
                sx={styles.customCancelBookingCancelButton}
                onClick={onClose}
              >
                No
              </Button>
              <Button
                color="primary"
                sx={styles.customCancelBookingConfirmButton}
                onClick={(evt) => {
                  handleDeleteClick(evt, supplier.supplierId)
                  onClose()
                }}
              >
                Yes, Delete it!
              </Button>
            </div>
          </Paper>
        )
      },
    })
  }

  return (
    <BaseLayout
      pageTitle="Supplier Management"
      pageSubtitle="Your company details"
      mainElement={
        <MaterialTable
          title="Suppliers"
          columns={[
            { title: 'supplierId', field: 'supplierId', hidden: true },
            {
              title: 'Account Number',
              field: 'accountNumber',
            },
            {
              title: 'Trading Name',
              field: 'alias',
              hidden: true,
            },
            {
              title: 'Company Name',
              field: 'companyName',
              cellStyle: { width: 1000, maxWidth: 1000 },
              headerStyle: { width: 1000, maxWidth: 1000 },
              defaultSort: 'asc',
              render: (rowData: any) =>
                rowData.alias ? (
                  <>
                    {rowData.alias}{' '}
                    <span style={{ color: 'GrayText' }}>
                      ({rowData.companyName})
                    </span>
                  </>
                ) : (
                  <span>{rowData.companyName}</span>
                ),
            },
            {
              title: 'Registration Number',
              field: 'registrationNumber',
              render: (rowData: any) =>
                rowData.registrationNumber ? (
                  <>{rowData.registrationNumber}</>
                ) : (
                  <span>-</span>
                ),
            },
            { title: 'Region', field: 'regionName' },
            { title: 'Status', field: 'status' },
          ]}
          options={{
            headerStyle: {
              backgroundColor: headerBackgroundColor,
              color: headerTextColor,
              padding: '16px',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
            },
            rowStyle: {
              backgroundColor: '#ffffff',
              fontSize: 'var(--fs-12, 0.75rem)',
            },
            grouping: true,
            emptyRowsWhenPaging: false,
            pageSize: 10,
            pageSizeOptions: [10, 50, 100],
            actionsColumnIndex: -1,
          }}
          localization={{
            pagination: {
              labelRowsPerPage: '',
            },
          }}
          style={{
            backgroundColor: '#ededed',
            width: '100%',
            // wordWrap: false
          }}
          actions={[
            {
              icon: () => <DvrSharpIcon fontSize="small" color="action" />,
              tooltip: 'View Supplier',
              onClick: (event: any, rowData: any) =>
                handleEditClick(rowData.supplierId),
            },
            {
              icon: () => <ClearSharpIcon fontSize="small" color="error" />,
              tooltip: 'Delete Supplier',
              onClick: (event: any, rowData: any) =>
                handleClickOpenCancel(event, rowData),
            },
          ]}
          data={tableData}
          isLoading={isLoading}
        />
      }
    />
  )
}
export default SupplierSearchPage
